import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  isMin1280pxWide,
  isMin768pxWide,
} from '../../../utils/device/identifyDevice';

const peepHideThresholdPx = 150;
const peepShowThresholdPx = 100;

type UseHeaderVisibilityParams = {
  hasStickyHeader: boolean;
  isFocused: boolean;
  onlyPeepAtTop: boolean;
  showNav: boolean;
  dispatchPeepEvent: (peeped: boolean) => void;
  dispatchStickyHeaderEvent: (peeped: boolean) => void;
};

type UseHeaderVisibilityResult = {
  isVisible: boolean;
  stickyHeaderActive: boolean;
  handleUnpeep: () => void;
  forceStickyHeaderInactive: () => void;
};

export const useHeaderVisibility = ({
  hasStickyHeader,
  isFocused,
  onlyPeepAtTop,
  showNav,
  dispatchPeepEvent,
  dispatchStickyHeaderEvent,
}: UseHeaderVisibilityParams): UseHeaderVisibilityResult => {
  const [state, setState] = useState({
    isVisible: true,
    previousDirectionChangePx: 0,
    previousScrollPositionPx: 0,
    stickyHeaderActive: false,
    wasScrollingUp: false,
  });

  const scrollPositionPxRef = useRef(0);
  const directionChangePxRef = useRef(0);
  const wasScrollingUpRef = useRef(false);
  const stickyHeaderActiveRef = useRef(false);
  const isVisibleRef = useRef(true);
  const showNavRef = useRef(showNav);
  const isFocusedRef = useRef(isFocused);

  const useIsomorphicLayoutEffect =
    typeof window !== 'undefined' ? useLayoutEffect : useEffect;

  showNavRef.current = showNav;
  isFocusedRef.current = isFocused;

  const forceStickyHeaderInactive = useCallback(() => {
    stickyHeaderActiveRef.current = false;
    setState(prevState => ({ ...prevState, stickyHeaderActive: false }));
  }, []);

  const setVisibility = useCallback(() => {
    if (isMin1280pxWide() && !hasStickyHeader) {
      if (!isVisibleRef.current) {
        setState(prevState => ({ ...prevState, isVisible: true }));
        isVisibleRef.current = true;
      }
      return;
    }

    const currentScrollPositionPx = window.pageYOffset;
    const previousScrollPositionPx = scrollPositionPxRef.current;
    const previousDirectionChangePx = directionChangePxRef.current;

    const isCurrentlyScrollingUp =
      currentScrollPositionPx < previousScrollPositionPx;

    const newVisibilityState: {
      isVisible?: boolean;
      stickyHeaderActive?: boolean;
    } = {};

    const scrollDirectionDidChange = () =>
      wasScrollingUpRef.current !== isCurrentlyScrollingUp;

    const scrollPositionOffTopOfPage = () => currentScrollPositionPx < 0;

    const headerAtTopOfScreen = () =>
      currentScrollPositionPx < peepHideThresholdPx;

    const updatePreviousDirectionChangePx = () => {
      if (scrollDirectionDidChange()) {
        return scrollPositionOffTopOfPage() || showNavRef.current
          ? previousDirectionChangePx
          : currentScrollPositionPx;
      }
      return previousDirectionChangePx;
    };

    const scrolledUpPastPeepThreshold = () => {
      const showThresholdPx = previousDirectionChangePx - peepShowThresholdPx;

      let showOnScrollUp = isCurrentlyScrollingUp;
      if (onlyPeepAtTop) {
        showOnScrollUp = false;
      }

      return (
        !isVisibleRef.current &&
        showOnScrollUp &&
        currentScrollPositionPx < showThresholdPx
      );
    };

    const scrolledDownPastPeepThreshold = () => {
      const hideThresholdPx = previousDirectionChangePx + peepHideThresholdPx;

      return (
        isVisibleRef.current &&
        !isCurrentlyScrollingUp &&
        currentScrollPositionPx > hideThresholdPx
      );
    };

    const showHeader = () => {
      newVisibilityState.isVisible = true;
      dispatchPeepEvent(true);
    };

    const hideHeader = () => {
      newVisibilityState.isVisible = false;
      dispatchPeepEvent(false);
    };

    const showStickyHeader = () => {
      newVisibilityState.stickyHeaderActive = true;
      if (!stickyHeaderActiveRef.current) {
        dispatchStickyHeaderEvent(true);
      }
    };

    const hideStickyHeader = () => {
      newVisibilityState.stickyHeaderActive = false;
      if (stickyHeaderActiveRef.current) {
        dispatchStickyHeaderEvent(false);
      }
    };

    if (isMin768pxWide() && hasStickyHeader) {
      if (!isVisibleRef.current) {
        newVisibilityState.isVisible = true;
      }

      if (scrolledDownPastPeepThreshold()) {
        showStickyHeader();
      }

      if (headerAtTopOfScreen()) {
        hideStickyHeader();
      }
    } else {
      if (stickyHeaderActiveRef.current) {
        hideStickyHeader();
      }

      if (scrolledUpPastPeepThreshold() || headerAtTopOfScreen()) {
        showHeader();
      }

      if (scrolledDownPastPeepThreshold()) {
        hideHeader();
      }
    }

    const newDirectionChangePx = updatePreviousDirectionChangePx();
    const newScrollPositionPx =
      currentScrollPositionPx < 0 ? 0 : currentScrollPositionPx;

    directionChangePxRef.current = newDirectionChangePx;
    scrollPositionPxRef.current = newScrollPositionPx;
    wasScrollingUpRef.current = isCurrentlyScrollingUp;

    if ('stickyHeaderActive' in newVisibilityState) {
      stickyHeaderActiveRef.current = newVisibilityState.stickyHeaderActive!;
    }
    if ('isVisible' in newVisibilityState) {
      isVisibleRef.current = newVisibilityState.isVisible!;
    }

    setState(prevState => ({
      ...prevState,
      ...newVisibilityState,
      previousDirectionChangePx: newDirectionChangePx,
      previousScrollPositionPx: newScrollPositionPx,
      wasScrollingUp: isCurrentlyScrollingUp,
    }));
  }, [
    dispatchPeepEvent,
    dispatchStickyHeaderEvent,
    hasStickyHeader,
    onlyPeepAtTop,
  ]);

  const syncView = useCallback(() => {
    if (!isFocusedRef.current) {
      setVisibility();
    }
  }, [setVisibility]);

  const updateView = useCallback(() => {
    if (!isFocusedRef.current) {
      requestAnimationFrame(() => {
        setVisibility();
      });
    }
  }, [setVisibility]);

  useIsomorphicLayoutEffect(() => {
    syncView();
  }, [syncView]);

  useEffect(
    function listenForScroll() {
      window.addEventListener('scroll', updateView);

      return function stopListeningForScroll() {
        window.removeEventListener('scroll', updateView);
      };
    },
    [updateView]
  );

  const handleUnpeep = useCallback(() => {
    if (!isMin1280pxWide() && window.pageYOffset >= peepHideThresholdPx) {
      dispatchPeepEvent(false);
      isVisibleRef.current = false;
      setState(prevState => ({ ...prevState, isVisible: false }));
    }
  }, [dispatchPeepEvent]);

  return {
    isVisible: state.isVisible,
    stickyHeaderActive: state.stickyHeaderActive,
    handleUnpeep,
    forceStickyHeaderInactive,
  };
};
